import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ExcelJS from "exceljs";
// import { saveAs } from "file-saver";
import { Form, Formik } from "formik";
import { UploadImageIcon } from "../../assets/icons";
import PageHeader from "../../components/PageHeader";
import { useSidebar } from "../../context/SidebarContext";
import { useNavigate } from "react-router-dom";
import { routes } from "../../helpers/constants";
import { reqToImportCode } from "../../redux/slices.js/discountmanagement/discountPromotionSlice";

const ImportCSV = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setIsSidebarOpen } = useSidebar();
  const [step, setStep] = useState(1);
  const [fileName, setFileName] = useState("");
  const [fileData, setFileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [parsedHeaders, setParsedHeaders] = useState([]);
  const [parsedRows, setParsedRows] = useState([]);

  // Use ref to store the latest dropdown data
  const dropdownDataRef = useRef({
    programNames: [],
    discount_program_types: [],
    discount_channals: [],
    discount_scope: [],
    discount_status: [],
  });

  const coupons = useSelector(
    (state) => state?.discountCoupons?.discount?.data
  );

  const {
    discount_channals = [],
    discount_program_types = [],
    discount_scope = [],
    discount_status = [],
    programNames = [],
  } = coupons || {};

  // Update ref when data changes
  useEffect(() => {
    dropdownDataRef.current = {
      programNames,
      discount_program_types,
      discount_channals,
      discount_scope,
      discount_status,
    };
  }, [
    programNames,
    discount_program_types,
    discount_channals,
    discount_scope,
    discount_status,
  ]);

  // Download Excel Template with discount code fields
  const downloadTemplate = async () => {
    try {
      setIsLoading(true);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Discount Codes");

      // Add instructions as the first row
      worksheet.addRow([
        "INSTRUCTIONS: Fill data in rows below. Use dropdowns in columns F, G, H, J, K. For Scope (I), enter comma-separated values (no dropdown).",
      ]);
      const instructionRow = worksheet.getRow(1);
      instructionRow.font = {
        bold: true,
        italic: true,
        color: { argb: "FFFF0000" },
      };
      instructionRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFFCC" },
      };
      worksheet.mergeCells("A1:K1");

      // Headers for discount codes (now row 2)
      const headers = [
        "Pre",
        "Number Uses Per",
        "Start",
        "Expiry",
        "Condition",
        "Program",
        "Type",
        "Channel",
        "Scope",
        "Status",
        "Is Abuse",
      ];

      // Add headers row (row 2)
      const headerRow = worksheet.addRow(headers);

      // Style the header row
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF4285F4" },
      };

      // Add 50 empty rows for data entry (rows 3 to 52)
      for (let i = 0; i < 50; i++) {
        worksheet.addRow(new Array(headers.length).fill(""));
      }

      // Helper to add dropdown validation
      const addDropdown = (colIndex, options, allowBlank = true) => {
        if (!options || options.length === 0) {
          console.warn(`No options available for column index ${colIndex}`);
          return;
        }

        const columnLetter = String.fromCharCode(65 + colIndex); // A=0, B=1, etc.
        // Apply dropdown to data rows (row 3 to 52)
        worksheet.dataValidations.add(`${columnLetter}3:${columnLetter}52`, {
          type: "list",
          allowBlank: allowBlank,
          formulae: [`"${options.map((opt) => opt.name).join(",")}"`],
          showErrorMessage: true,
          errorStyle: "warning",
          errorTitle: "Invalid Selection",
          error: "Please select a value from the dropdown list",
        });
      };

      // Get current dropdown data from ref
      const {
        programNames: currentProgramNames,
        discount_program_types: currentProgramTypes,
        discount_channals: currentChannels,
        discount_scope: currentScope,
        discount_status: currentStatus,
      } = dropdownDataRef.current;

      // Add dropdowns for each field with current data
      if (currentProgramNames?.length) {
        addDropdown(5, currentProgramNames); // Program (Column F, index 5)
      } else {
        console.warn("No program names available for dropdown");
      }

      if (currentProgramTypes?.length) {
        addDropdown(6, currentProgramTypes); // Type (Column G, index 6)
      } else {
        console.warn("No program types available for dropdown");
      }

      if (currentChannels?.length) {
        addDropdown(7, currentChannels); // Channel (Column H, index 7)
      } else {
        console.warn("No channels available for dropdown");
      }

      // For Scope (Column I, index 8), no dropdown since multi-select; add note instead
      if (currentScope?.length) {
        const scopeHeaderCell = worksheet.getCell("I2");
        scopeHeaderCell.note = {
          texts: [
            {
              text: `Multiple values allowed. Use comma separation (e.g., "${currentScope
                .slice(0, 2)
                .map((s) => s.name)
                .join(",")}"). Available options: ${currentScope
                .map((s) => s.name)
                .join(", ")}.`,
            },
          ],
        };
      } else {
        console.warn("No scope options available");
      }

      if (currentStatus?.length) {
        addDropdown(9, currentStatus); // Status (Column J, index 9)
      } else {
        console.warn("No status options available for dropdown");
      }

      // Add dropdown for Is Abuse field (Column K, index 10)
      const isAbuseOptions = [
        { id: 1, name: "Yes" },
        { id: 0, name: "No" },
      ];
      addDropdown(10, isAbuseOptions);

      // Set column widths
      worksheet.columns = [
        { width: 15 }, // Pre (A)
        { width: 15 }, // Number Uses Per (B)
        { width: 12 }, // Start (C)
        { width: 12 }, // Expiry (D)
        { width: 20 }, // Condition (E)
        { width: 25 }, // Program (F)
        { width: 20 }, // Type (G)
        { width: 15 }, // Channel (H)
        { width: 20 }, // Scope (I)
        { width: 15 }, // Status (J)
        { width: 12 }, // Is Abuse (K)
      ];

      // Freeze the header rows (instructions + headers)
      worksheet.views = [{ state: "frozen", xSplit: 0, ySplit: 2 }];

      // Generate and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "Discount_Codes_Import_Template.xlsx");
    } catch (error) {
      console.error("Error generating template:", error);
      alert("Error generating template. Please check console for details.");
    } finally {
      setIsLoading(false);
    }
  };

  // Map dropdown label -> value (ID) for API
  const mapDropdownLabelToValue = (header, input) => {
    if (!input || input === "") return "";

    const mappingTable = {
      Program: programNames,
      Type: discount_program_types,
      Channel: discount_channals,
      Scope: discount_scope,
      Status: discount_status,
      "Is Abuse": [
        { id: 1, name: "Yes" },
        { id: 0, name: "No" },
      ],
    };

    const options = mappingTable[header];
    if (!options) return input;

    // Handle multi-select for Scope field (comma-separated values)
    if (header === "Scope" && input.includes(",")) {
      const selectedValues = input.split(",").map((val) => val.trim());
      const mappedValues = selectedValues.map((val) => {
        const option = options.find((o) => o.name === val);
        return option ? option.id : val;
      });
      return mappedValues.join(",");
    }

    // Find by name first
    const byName = options.find((o) => o.name === input);
    if (byName) return byName.id;

    // If input is already an ID, return it
    if (!isNaN(input)) return parseInt(input);

    return input;
  };

  // Rest of your component remains the same...
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setFileData(file);

    try {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await file.arrayBuffer());
      const worksheet = workbook.worksheets[0];

      // Find the header row (skip instruction row if exists)
      let headerRowIndex = 1;
      if (
        worksheet
          .getRow(1)
          .getCell(1)
          .value?.toString()
          .includes("INSTRUCTIONS")
      ) {
        headerRowIndex = 2;
      }

      const headers = worksheet.getRow(headerRowIndex).values.slice(1); // skip 0 index
      const rows = [];

      worksheet.eachRow((row, rowIndex) => {
        // Skip header row and any rows above it
        if (rowIndex <= headerRowIndex) return;

        const record = {};
        headers.forEach((header, i) => {
          if (!header) return;

          // Extract value from cell, handling hyperlink objects
          let cellValue = row.values[i + 1] || "";

          // If it's a hyperlink object, get the text property
          if (
            cellValue &&
            typeof cellValue === "object" &&
            cellValue.text !== undefined
          ) {
            cellValue = cellValue.text;
          }

          record[header] = cellValue;
        });

        // Only push if row has at least one non-empty value
        const hasData = Object.values(record).some(
          (v) => String(v).trim() !== ""
        );
        if (hasData) rows.push(record);
      });

      setParsedHeaders(headers.filter(Boolean));
      setParsedRows(rows);
    } catch (err) {
      console.error("Failed to parse Excel:", err);
    }

    setStep(2);
  };

  // Handle form submission
  const handleSubmit = (values) => {
    setStep(3);
  };

  const handleCsvUpload = () => {
    if (!fileData) {
      alert("No file found. Please upload the template again.");
      return;
    }

    // Prepare the data with mapped IDs
    const mappedData = parsedRows.map((row) => {
      const mappedRow = {};
      parsedHeaders.forEach((header) => {
        let val = row[header] ?? "";
        // Ensure we're working with a string, not an object
        if (val && typeof val === "object") {
          val = val.text || JSON.stringify(val);
        }
        mappedRow[header] = mapDropdownLabelToValue(header, val);
      });
      return mappedRow;
    });

    dispatch(
      reqToImportCode({
        file: fileData,
        data: mappedData, // Pass the mapped data
        onSuccess: (res) => {
          navigate(routes.employeeList); // Update this route as needed
        },
        onFailure: (err) => {
          console.error("❌ Import failed:", err);
          alert("Import failed. Please check the console for details.");
        },
      })
    );
  };

  return (
    <section className="dashboard-page main-layout-page main-layout rounded-md border-[1px] border-solid border-[#F3EFEF] space-y-8">
      <div className="!p-0">
        <div className="dashboard-page main-layout-page main-layout ">
          <div className="bg-[#F5FFBD] text-[#4285f4] md:px-6 px-3 pt-3 md:pt-[50px] !pd-0 w-full flex-shrink-0 page-header">
            <PageHeader
              title="Import Codes"
              description="Bulk import Codes using Excel template"
              onOpenSidebar={() => setIsSidebarOpen(true)}
            />
          </div>
          <div className="flex-1 right-sidebar relative sm:p-6 p-3">
            <div className="right-sidebar-contend pt-6">
              <div className="flex gap-2 mb-6">
                <div
                  className={`h-[10px] flex-1 rounded-[2.5px] ${
                    step >= 1 ? "bg-blue" : "bg-[#DFF2FF]"
                  }`}
                />
                <div
                  className={`h-[10px] flex-1 rounded-[2.5px] ${
                    step >= 2 ? "bg-blue" : "bg-[#DFF2FF]"
                  }`}
                />
                <div
                  className={`h-[10px] flex-1 rounded-[2.5px] ${
                    step === 3 ? "bg-blue" : "bg-[#DFF2FF]"
                  }`}
                />
              </div>

              {step === 1 && (
                <div className="signin-form flex flex-col h-[calc(100%-25px)] gap-4">
                  <div className="chart-card">
                    <h2 className="text-h4 text-blue">Import Instructions</h2>

                    <div className="mt-4 p-4 bg-blue-50 rounded-md">
                      <h3 className="font-bold text-blue mb-2">
                        Important Notes:
                      </h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>
                          Download the template and fill in your discount code
                          data
                        </li>
                        <li>
                          <strong>
                            Dropdown fields are available in columns F, G, H, J,
                            K
                          </strong>{" "}
                          - click on any cell in these columns to see the
                          dropdown arrow
                        </li>
                        <li>
                          Dates must be in <strong>YYYY-MM-DD</strong> format
                          (e.g., 2024-12-31)
                        </li>
                        <li>
                          <strong>
                            Scope field supports multiple selection
                          </strong>{" "}
                          - type comma-separated values (e.g.,
                          "WELCOME,BILLING_CYCLE"). No dropdown for this field;
                          refer to the comment on the header for available
                          options.
                        </li>
                        <li>
                          A maximum of 50 discount codes can be added in a
                          single upload. You can add more rows in Excel if
                          needed.
                        </li>
                      </ul>

                      <h3 className="font-bold text-blue mt-4 mb-2">
                        Available Dropdown Options:
                      </h3>
                      <ul className="list-disc pl-5 space-y-1 text-sm">
                        <li>
                          <strong>Program:</strong>{" "}
                          {programNames
                            .slice(0, 3)
                            .map((p) => p.name)
                            .join(", ")}
                          ... ({programNames.length} options)
                        </li>
                        <li>
                          <strong>Type:</strong>{" "}
                          {discount_program_types
                            .slice(0, 3)
                            .map((t) => t.name)
                            .join(", ")}
                          ... ({discount_program_types.length} options)
                        </li>
                        <li>
                          <strong>Channel:</strong>{" "}
                          {discount_channals
                            .slice(0, 3)
                            .map((c) => c.name)
                            .join(", ")}
                          ... ({discount_channals.length} options)
                        </li>
                        <li>
                          <strong>Scope:</strong>{" "}
                          {discount_scope
                            .slice(0, 3)
                            .map((s) => s.name)
                            .join(", ")}
                          ... ({discount_scope.length} options) - Multiple
                          select (type comma-separated)
                        </li>
                        <li>
                          <strong>Status:</strong>{" "}
                          {discount_status
                            .slice(0, 3)
                            .map((s) => s.name)
                            .join(", ")}
                          ... ({discount_status.length} options)
                        </li>
                        <li>
                          <strong>Is Abuse:</strong> Yes, No
                        </li>
                      </ul>
                    </div>

                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={downloadTemplate}
                        className="btn-light-blue"
                        disabled={isLoading}
                      >
                        {isLoading
                          ? "Preparing Template..."
                          : "Download Excel Template"}
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-auto flex-wrap">
                    <button
                      type="button"
                      className="btn-light-blue"
                      onClick={() => setStep(2)}
                      disabled={isLoading}
                    >
                      Next: Upload File
                    </button>
                  </div>
                </div>
              )}

              {/* Steps 2 and 3 remain the same */}
              {step === 2 && (
                <Formik initialValues={{}} onSubmit={handleSubmit}>
                  <Form className="signin-form flex flex-col h-[calc(100%-25px)] gap-4">
                    <div className="chart-card">
                      <h2 className="text-h4 text-blue">
                        Upload Filled Template
                      </h2>
                      <div className="mt-3">
                        <label
                          htmlFor="fileInput"
                          className="border border-[#1B3C5233] !m-0 rounded-lg w-full flex flex-col items-center justify-center p-4 md:p-10 text-center cursor-pointer"
                        >
                          <div className="flex items-center justify-center mb-4">
                            {UploadImageIcon}
                          </div>
                          <p className="!font-bold text-blue text-p">
                            {fileName || "Click to upload Excel file"}
                          </p>
                          <input
                            type="file"
                            id="fileInput"
                            className="hidden"
                            accept=".xlsx,.xls"
                            onChange={handleFileUpload}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-auto flex-wrap">
                      <button
                        type="submit"
                        className="btn-light-blue"
                        disabled={!fileData}
                      >
                        Next: Review Import
                      </button>
                      <button
                        type="button"
                        className="btn-light-gray"
                        onClick={() => setStep(1)}
                      >
                        Back
                      </button>
                    </div>
                  </Form>
                </Formik>
              )}

              {step === 3 && (
                <>
                  <div className="chart-card h-[calc(100%-25px)] flex flex-col">
                    <h2 className="text-h4 text-blue">Review Import</h2>

                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-md mt-3">
                      <p className="text-yellow-800 text-sm font-medium">
                        📋 Showing sample data (first code only) for review. All{" "}
                        {parsedRows.length} records will be processed during
                        import.
                      </p>
                    </div>

                    <div className="mt-4 flex-1 overflow-auto">
                      {parsedRows?.length ? (
                        <div className="space-y-6">
                          {parsedRows.slice(0, 1).map((row, rIdx) => (
                            <div
                              key={rIdx}
                              className="border border-gray-300 rounded-md p-4"
                            >
                              <h3 className="font-bold text-blue mb-3">
                                📄 Sample Discount Code Data (First Record)
                              </h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {Object.entries(row).map(([key, value]) => {
                                  let displayValue = value ?? "";
                                  if (
                                    displayValue &&
                                    typeof displayValue === "object"
                                  ) {
                                    displayValue =
                                      displayValue.text ||
                                      JSON.stringify(displayValue);
                                  }

                                  const mappedValue = mapDropdownLabelToValue(
                                    key,
                                    displayValue
                                  );
                                  const isDropdownChanged =
                                    String(displayValue) !==
                                    String(mappedValue);

                                  return (
                                    <div key={key} className="flex flex-col">
                                      <span className="font-medium text-gray-700">
                                        {key}:
                                      </span>
                                      <div className="flex flex-col">
                                        <span>{displayValue}</span>
                                        {isDropdownChanged && (
                                          <span className="text-xs text-[#888076]">
                                            → Mapped to ID: {mappedValue}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}

                          <div className="bg-blue-50 p-4 rounded-md">
                            <p className="text-blue font-medium">
                              📊 Total records ready for import:{" "}
                              <strong>{parsedRows.length}</strong>
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              Only the first record is shown above as a sample.
                              Click "Confirm Import" to process all{" "}
                              {parsedRows.length} discount codes.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sub_span">
                          No data found in uploaded file. Please go back and
                          upload again.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-auto flex-wrap pt-4">
                    <button
                      type="button"
                      className="btn-light-blue"
                      onClick={handleCsvUpload}
                    >
                      Confirm Import ({parsedRows.length} records)
                    </button>
                    <button
                      type="button"
                      className="btn-light-gray"
                      onClick={() => setStep(2)}
                    >
                      Back
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImportCSV;
